import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { fetchInviteByToken, acceptInvite } from '../lib/supabase';

export default function JoinTeamPage() {
  const [searchParams]  = useSearchParams();
  const { user, isLoading } = useAuth();
  const { loadTeams, switchTeam } = useTeam();
  const navigate        = useNavigate();

  const token = searchParams.get('token');

  const [invite, setInvite]   = useState(null);
  const [step, setStep]       = useState('loading'); // loading | preview | joining | done | error
  const [errorMsg, setErrorMsg] = useState('');

  // Load invite details
  useEffect(() => {
    if (!token) { setStep('error'); setErrorMsg('No invite token found.'); return; }
    fetchInviteByToken(token)
      .then(data => { setInvite(data); setStep('preview'); })
      .catch(() => { setStep('error'); setErrorMsg('This invite link is invalid or has expired.'); });
  }, [token]);

  const handleAccept = async () => {
    if (!user) {
      // Redirect to auth with return URL
      navigate(`/auth?redirect=/join?token=${token}`);
      return;
    }
    setStep('joining');
    try {
      const team = await acceptInvite(token, user.id);
      await loadTeams();
      await switchTeam(team.id);
      setStep('done');
      setTimeout(() => navigate('/app'), 2000);
    } catch (e) {
      setErrorMsg(e.message);
      setStep('error');
    }
  };

  if (isLoading || step === 'loading') return (
    <div style={s.page}><div style={s.center}>Loading...</div></div>
  );

  return (
    <div style={s.page}>
      <div style={s.card}>
        <Link to="/" style={s.logo}>
          CopyPaste<span style={{ color: '#7f77dd' }}>Genius</span>
        </Link>

        {step === 'preview' && invite && (
          <>
            <div style={{ fontSize: 40, marginBottom: 16 }}>👥</div>
            <div style={s.title}>You're invited!</div>
            <div style={s.sub}>
              You've been invited to join{' '}
              <strong style={{ color: 'var(--text)' }}>{invite.teams?.name}</strong>{' '}
              as a <strong style={{ color: '#afa9ec' }}>
                {invite.role === 'admin' ? 'Admin' : 'Member'}
              </strong>.
            </div>

            <div style={s.teamBox}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>
                {invite.teams?.avatar_url
                  ? <img src={invite.teams.avatar_url} alt="" style={{ width: 40, height: 40, borderRadius: 8 }} />
                  : '🏢'}
              </div>
              <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text)' }}>
                {invite.teams?.name}
              </div>
            </div>

            {!user && (
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16, textAlign: 'center' }}>
                You'll need to sign in or create an account to accept.
              </div>
            )}

            <button onClick={handleAccept} style={s.acceptBtn}>
              {user ? 'Accept invitation →' : 'Sign in to accept →'}
            </button>

            <div style={{ fontSize: 11, color: 'var(--dim)', textAlign: 'center', marginTop: 12 }}>
              Invite expires {new Date(invite.expires_at).toLocaleDateString()}
            </div>
          </>
        )}

        {step === 'joining' && (
          <>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
            <div style={s.title}>Joining team...</div>
          </>
        )}

        {step === 'done' && (
          <>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
            <div style={s.title}>You're in!</div>
            <div style={s.sub}>
              Welcome to <strong style={{ color: 'var(--text)' }}>{invite?.teams?.name}</strong>.
              Taking you to the app...
            </div>
          </>
        )}

        {step === 'error' && (
          <>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
            <div style={s.title}>Invite unavailable</div>
            <div style={s.sub}>{errorMsg}</div>
            <Link to="/app" style={s.acceptBtn}>Go to app</Link>
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh', background: 'var(--bg)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'var(--font)', padding: 24,
  },
  card: {
    background: 'var(--surface)', border: '0.5px solid var(--border2)',
    borderRadius: 16, padding: '36px 32px', maxWidth: 420, width: '100%',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0,
  },
  center: { textAlign: 'center', color: 'var(--muted)', fontSize: 14, padding: 40 },
  logo: {
    fontSize: 18, fontWeight: 500, color: 'var(--text)', textDecoration: 'none',
    letterSpacing: '-0.03em', marginBottom: 32,
  },
  title: {
    fontSize: 22, fontWeight: 500, color: 'var(--text)',
    letterSpacing: '-0.02em', marginBottom: 8, textAlign: 'center',
  },
  sub: {
    fontSize: 14, color: 'var(--muted)', lineHeight: 1.7,
    textAlign: 'center', marginBottom: 24,
  },
  teamBox: {
    background: 'var(--surface2)', border: '0.5px solid var(--border)',
    borderRadius: 12, padding: '20px 24px', textAlign: 'center',
    width: '100%', marginBottom: 20,
  },
  acceptBtn: {
    display: 'block', width: '100%', textAlign: 'center', textDecoration: 'none',
    background: '#7f77dd', color: '#fff', border: 'none',
    borderRadius: 8, padding: '12px', fontFamily: 'var(--font)',
    fontSize: 14, fontWeight: 500, cursor: 'pointer',
  },
};
